/* global gsap, ScrollTrigger */
/* ==========================================================================
   TOUR STAFF — sections/cpg-tour-staff.liquid
   Tabs, bag dialogs (equipment fetched from /products/<handle>.json),
   ?pro_view=first_last deep link, and GSAP motion.
   GSAP + ScrollTrigger are optional: everything works without them and
   motion is skipped when the visitor prefers reduced motion.
   ========================================================================== */

(() => {
  const root = document.querySelector('[data-tour-staff]');
  if (!root) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGsap = typeof gsap !== 'undefined';
  const motion = hasGsap && !reducedMotion;
  const hasScrollTrigger = motion && typeof ScrollTrigger !== 'undefined';
  const routesRoot = window.Shopify?.routes?.root || '/';

  if (hasScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  // ===== INTRO + CARD REVEAL =====
  if (motion) {
    gsap.from(root.querySelectorAll('[data-ts-intro]'), {
      y: 24,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
      stagger: 0.08,
      clearProps: 'transform,opacity',
    });
  }

  if (hasScrollTrigger) {
    // Only the panel showing on load scroll-reveals; other tabs stagger in when opened (see revealPanel)
    const cards = root.querySelectorAll('.tour-section:not([hidden]) [data-ts-card]');
    gsap.set(cards, { y: 32, opacity: 0 });
    ScrollTrigger.batch(cards, {
      start: 'top 92%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'power3.out',
          stagger: 0.06,
          overwrite: true,
          clearProps: 'transform',
        }),
    });
  }

  // ===== TABS =====
  const tablist = root.querySelector('[data-ts-tabs]');
  const tabs = [...root.querySelectorAll('.tour-tab[data-tab]')];
  const panels = [...root.querySelectorAll('[role="tabpanel"]')];
  const indicator = root.querySelector('[data-ts-indicator]');

  function moveIndicator(tab, animate) {
    if (!indicator || !tab) return;
    const props = { x: tab.offsetLeft, width: tab.offsetWidth };
    if (animate && motion) {
      gsap.to(indicator, { ...props, duration: 0.45, ease: 'power3.out' });
    } else if (hasGsap) {
      gsap.set(indicator, props);
    } else {
      indicator.style.transform = `translateX(${props.x}px)`;
      indicator.style.width = `${props.width}px`;
    }
  }

  function revealPanel(panel) {
    if (!motion) return;
    const cards = panel.querySelectorAll('[data-ts-card]');
    gsap.killTweensOf(cards);
    gsap.fromTo(
      cards,
      { y: 24, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.55,
        ease: 'power3.out',
        stagger: { amount: Math.min(0.6, cards.length * 0.04) },
        clearProps: 'transform',
      }
    );
  }

  function activateTab(tab, { focus = false } = {}) {
    if (!tab || tab.classList.contains('active')) {
      if (focus) tab?.focus();
      return;
    }

    tabs.forEach((t) => {
      const isActive = t === tab;
      t.classList.toggle('active', isActive);
      t.setAttribute('aria-selected', String(isActive));
      t.tabIndex = isActive ? 0 : -1;
    });

    const target = document.getElementById(tab.dataset.tab);
    panels.forEach((p) => {
      if (p !== target) p.setAttribute('hidden', '');
    });
    if (target) {
      target.removeAttribute('hidden');
      revealPanel(target);
      if (hasScrollTrigger) ScrollTrigger.refresh();
    }

    moveIndicator(tab, true);
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: motion ? 'smooth' : 'auto' });
    if (focus) tab.focus();
  }

  if (tablist && tabs.length) {
    tabs.forEach((tab) => tab.addEventListener('click', () => activateTab(tab)));

    // Arrow keys move between tabs (WAI-ARIA tabs pattern, automatic activation)
    tablist.addEventListener('keydown', (e) => {
      const current = tabs.indexOf(document.activeElement);
      if (current === -1) return;
      let next;
      if (e.key === 'ArrowRight') next = (current + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      else return;
      e.preventDefault();
      activateTab(tabs[next], { focus: true });
    });

    const placeIndicator = () => moveIndicator(tablist.querySelector('.tour-tab.active'), false);
    placeIndicator();
    tablist.classList.add('is-ready');
    // Fonts can change tab widths after first paint
    document.fonts?.ready.then(placeIndicator);
    window.addEventListener('resize', placeIndicator, { passive: true });
  }

  // ===== DIALOG OPEN/CLOSE =====
  // Element to refocus on close; the native restore doesn't survive the animated close
  const openers = new WeakMap();

  // The dialog photo is lazy; start it early and wait (briefly) for it so it never pops in mid-animation
  function warmPhoto(dialog) {
    const photo = dialog?.querySelector('.tour-dialog__media img');
    if (photo && photo.loading !== 'eager') photo.loading = 'eager';
    return photo;
  }

  async function openDialog(dialog) {
    if (dialog.open || dialog.dataset.opening) return;
    dialog.dataset.opening = 'true';
    openers.set(dialog, document.activeElement);
    const equipGrid = dialog.querySelector('.tour-equipment-grid');
    if (equipGrid && !equipGrid.dataset.loaded) {
      equipGrid.dataset.loaded = 'true';
      let handles;
      try {
        handles = JSON.parse(equipGrid.dataset.equipment || '[]');
      } catch {
        handles = [];
      }
      fillEquipment(equipGrid, handles);
    }

    const photo = warmPhoto(dialog);
    if (photo && !photo.complete) {
      await Promise.race([photo.decode().catch(() => {}), new Promise((r) => setTimeout(r, 300))]);
    }

    dialog.showModal();
    delete dialog.dataset.opening;

    if (motion) {
      // Translate only: scaling the big photo re-rasterizes it and flickers when the tween ends
      const panel = dialog.querySelector('[data-ts-dialog-panel]');
      gsap.fromTo(dialog, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'power1.out' });
      gsap.fromTo(panel, { y: 16 }, { y: 0, duration: 0.35, ease: 'power2.out', clearProps: 'transform' });
      gsap.from(dialog.querySelectorAll('.tour-dialog__body > *'), {
        y: 12,
        opacity: 0,
        duration: 0.35,
        ease: 'power2.out',
        stagger: 0.04,
        delay: 0.08,
        clearProps: 'transform,opacity',
      });
    }
  }

  function finishClose(dialog) {
    dialog.close();
    const opener = openers.get(dialog);
    if (opener?.isConnected && opener !== document.body) opener.focus({ preventScroll: true });
  }

  function closeDialog(dialog) {
    if (!dialog.open || dialog.dataset.closing) return;
    if (!motion) {
      finishClose(dialog);
      return;
    }
    dialog.dataset.closing = 'true';
    dialog.classList.add('is-closing');
    gsap.killTweensOf(dialog);
    gsap.to(dialog, {
      opacity: 0,
      duration: 0.15,
      ease: 'power1.in',
      onComplete: () => {
        finishClose(dialog);
        dialog.classList.remove('is-closing');
        gsap.set(dialog, { clearProps: 'opacity' });
        delete dialog.dataset.closing;
      },
    });
  }

  // Open via card trigger (a <button>, so Enter/Space work natively)
  root.querySelectorAll('[data-dialog-target]').forEach((trigger) => {
    const dialog = document.getElementById(trigger.dataset.dialogTarget);
    if (!dialog) return;
    trigger.addEventListener('click', () => openDialog(dialog));
    // Hover/focus usually comes a few hundred ms before the click: enough to have the photo ready
    trigger.addEventListener('pointerenter', () => warmPhoto(dialog), { once: true });
    trigger.addEventListener('focus', () => warmPhoto(dialog), { once: true });
  });

  root.querySelectorAll('[data-ts-dialog]').forEach((dialog) => {
    dialog.querySelector('[data-ts-close]')?.addEventListener('click', () => closeDialog(dialog));

    // Backdrop click: the dialog itself is only the target outside the panel
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeDialog(dialog);
    });

    // Esc: animate out instead of the native instant close
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeDialog(dialog);
    });
  });

  // ===== EQUIPMENT FETCH =====
  function productImage(product) {
    const src = product.image?.src || product.images?.[0]?.src;
    if (!src) return null;
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '400');
      return url.toString();
    } catch {
      return src;
    }
  }

  function equipmentCard(product) {
    const item = document.createElement('li');
    item.className = 'tour-equipment-card';

    const link = document.createElement('a');
    link.className = 'tour-equipment-card__link';
    link.href = `${routesRoot}products/${product.handle}`;
    link.target = '_blank';

    const media = document.createElement('span');
    media.className = 'tour-equipment-card__img';
    const src = productImage(product);
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.width = 400;
      img.height = 400;
      img.loading = 'lazy';
      media.appendChild(img);
    }

    const title = document.createElement('span');
    title.className = 'tour-equipment-card__title';
    title.textContent = product.title;

    link.append(media, title);
    item.appendChild(link);
    return item;
  }

  async function fillEquipment(grid, handles) {
    const results = await Promise.all(
      handles.map((handle) =>
        fetch(`${routesRoot}products/${encodeURIComponent(handle)}.json`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    const items = results.filter((data) => data?.product).map((data) => equipmentCard(data.product));
    grid.replaceChildren(...items);
    grid.setAttribute('aria-busy', 'false');

    // Every handle gone (retired products): hide the empty heading
    if (!items.length) {
      grid.closest('[data-ts-equipment-wrap]')?.setAttribute('hidden', '');
      return;
    }

    if (motion) {
      gsap.from(items, {
        y: 16,
        opacity: 0,
        duration: 0.45,
        ease: 'power2.out',
        stagger: 0.05,
        clearProps: 'transform,opacity',
      });
    }
  }

  // ===== URL PARAM: ?pro_view=first_last =====
  const proPop = new URLSearchParams(window.location.search).get('pro_view');
  if (proPop) {
    const trigger = root.querySelector(`[data-name="${CSS.escape(proPop.toLowerCase())}"]`);
    if (trigger) {
      // Show the person's tab first so the page behind the dialog matches
      const panel = trigger.closest('[role="tabpanel"]');
      if (panel?.hidden) activateTab(tabs.find((t) => t.dataset.tab === panel.id));
      const dialog = trigger.dataset.dialogTarget && document.getElementById(trigger.dataset.dialogTarget);
      if (dialog) setTimeout(() => openDialog(dialog), 300);
    }
  }
})();
